import React, { useRef, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import type { Role } from '@/types';
import { RateSheetConfigForm } from '@/components/config_forms/roles/RateSheetConfigForm';
import type { ValidationError } from '@/utils/validation/types';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import { getFieldErrorElement } from '@/utils/validation/helpers';

interface ClientRolesConfigProps {
  formData: ClientFormData;
  onRolesChange: (roles: Role[]) => void;
  isCustomized: boolean;
  onRevert: () => void;
  userDefaultRoles?: Role[];
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  onValidityChange?: (isValid: boolean) => void;
  clientName?: string;
  // DayRateConfig props
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur: (field: keyof ClientFormData) => void;
  isLoading: boolean;
  isDayRateCustomized: boolean;
  onRevertDayRate: () => void;
}

export const ClientRolesConfig: React.FC<ClientRolesConfigProps> = ({
  formData,
  onRolesChange,
  isCustomized,
  onRevert,
  errors,
  touchedFields,
  onValidityChange,
  clientName,
  handleChange,
  onFieldBlur,
  isLoading,
  isDayRateCustomized,
  onRevertDayRate,
}) => {
  const { roles, minimum_billable_hours, half_day_hours } = formData;

  // Track editing state from RoleList for validity gating
  const isEditingRef = useRef(false);

  const handleEditingChange = React.useCallback((editing: boolean) => {
    isEditingRef.current = editing;
    onValidityChange?.(!editing);
  }, [onValidityChange]);

  // Also notify validity when roles change
  useEffect(() => {
    onValidityChange?.(!isEditingRef.current);
  }, [roles, onValidityChange]);

  const anyDayRateRole = (roles || []).some(
    (r) => r.billingStructure === 'day_rate'
  );

  const anyHalfDayRoleEnabled = (roles || []).some(
    (r) => r.billingStructure === 'day_rate' && r.allowHalfDay
  );

  // Detect identity changes (name or billing structure) when roles are updated
  // and strip the id so the backend creates a new entry for the client
  const prevRolesRef = useRef<Role[]>(roles || []);
  useEffect(() => { prevRolesRef.current = roles || []; }, [roles]);

  const handleRolesChange = React.useCallback((updatedRoles: Role[]) => {
    const processedRoles = updatedRoles.map((role, i) => {
      const original = prevRolesRef.current[i];
      if (!original || !original.id) return role;
      const identityChanged =
        original.name.trim().toLowerCase() !== role.name.trim().toLowerCase() ||
        original.billingStructure !== role.billingStructure;
      if (identityChanged) {
        const { id, ...roleWithoutId } = role;
        toast.info(`Rate for "${original.name}" is now a custom rate for this client.`);
        return roleWithoutId as Role;
      }
      return role;
    });
    onRolesChange(processedRoles);
  }, [onRolesChange]);

  const handleRemoveRole = React.useCallback((index: number) => {
    const currentRoles = roles || [];
    const roleToRemove = currentRoles[index];
    if (!roleToRemove) return;

    const updatedRoles = currentRoles.filter((_, i) => i !== index);
    onRolesChange(updatedRoles);
    const clientPart = clientName ? ` for "${clientName}"` : '';
    toast.success(`Removed rate "${roleToRemove.name}"${clientPart}`);
  }, [roles, onRolesChange, clientName]);

  return (
    <RateSheetConfigForm
      title="Roles"
      description="Override your default rate sheet for this specific client."
      roles={roles || []}
      onRemoveRole={handleRemoveRole}
      onRolesChange={handleRolesChange}
      onEditingChange={handleEditingChange}
      isCustomized={isCustomized}
      onRevertAll={onRevert}
      context="client"
      dayRateConfig={{
          title: "Day Rate Settings",
          description: "Configure billing parameters for day rate roles.",
          isMinimumBillableHoursDisabled: !anyDayRateRole,
          isHalfDayConfigDisabled: !anyHalfDayRoleEnabled,
          context: 'client',
          minimumBillableHours: minimum_billable_hours ?? null,
          halfDayHours: half_day_hours ?? null,
          validMinimumBillableHoursForHalfDay: minimum_billable_hours || 0,
          onMinimumBillableHoursChange: (v: number) => handleChange('minimum_billable_hours', v),
          onHalfDayHoursChange: (v: number) => handleChange('half_day_hours', v),
          minimumBillableHoursProps: {
            value: formData.minimum_billable_hours ?? '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('minimum_billable_hours', e.target.value ? parseFloat(e.target.value) : null),
            onBlur: () => onFieldBlur('minimum_billable_hours'),
            disabled: isLoading,
            'data-validation-key': 'minimum_billable_hours',
          },
          halfDayHoursProps: {
            value: formData.half_day_hours ?? '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('half_day_hours', e.target.value ? parseFloat(e.target.value) : null),
            onBlur: () => onFieldBlur('half_day_hours'),
            disabled: isLoading,
            'data-validation-key': 'half_day_hours',
          },
          minimumBillableHoursError: getFieldErrorElement('minimum_billable_hours', errors, touchedFields),
          halfDayHoursError: getFieldErrorElement('half_day_hours', errors, touchedFields),
          isCustomized: isDayRateCustomized,
          onRevert: onRevertDayRate,
        }}
    />
  );
};
